'use client';

import appShellStore from '@/lib/offline/appShellStore';
import { getApiUrl } from '@/lib/apiConfig';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

export interface PendingQuoteRequestPayload {
  item_type: string;
  title: string;
  description?: string;
  status: string;
  parent_id: string;
  contact_id?: string;
  contact_name?: string;
  custom_fields?: Record<string, unknown>;
}

export interface PendingQuoteRequest {
  id: string;
  payload: PendingQuoteRequestPayload;
  relationshipPayload?: {
    source_work_item_id: string;
    target_work_item_id: string;
    relationship_type: string;
  };
  createdAt: string;
  retryCount: number;
  lastError?: string;
}

const QUOTE_REQUEST_KEY = 'smarttask_pending_quote_requests';
const MAX_QUOTE_REQUEST_RETRIES = 5;

export async function loadPendingQuoteRequests(clientId: number): Promise<PendingQuoteRequest[]> {
  return appShellStore.loadSettingsArrayMigration<PendingQuoteRequest>(clientId, QUOTE_REQUEST_KEY);
}

export async function savePendingQuoteRequests(clientId: number, entries: PendingQuoteRequest[]): Promise<void> {
  await appShellStore.saveSettingsArrayMigration(clientId, QUOTE_REQUEST_KEY, entries);
}

export async function getPendingQuoteRequestCount(clientId: number): Promise<number> {
  if (!clientId) return 0;
  const entries = await loadPendingQuoteRequests(clientId);
  return entries.length;
}

export async function syncPendingQuoteRequests(params: {
  clientId: number;
  token: string;
  onProgress?: (progress: { current: number; total: number; message: string }) => void;
}): Promise<{ synced: number; failed: number; remaining: number }> {
  const { clientId, token, onProgress } = params;
  if (!clientId || !token) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const queue = await loadPendingQuoteRequests(clientId);
  if (queue.length === 0) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const remaining: PendingQuoteRequest[] = [];
  let synced = 0;
  let failed = 0;

  for (let index = 0; index < queue.length; index += 1) {
    const entry = queue[index];
    onProgress?.({
      current: index + 1,
      total: queue.length,
      message: `Syncing quote request ${index + 1} of ${queue.length}`,
    });

    try {
      // Create the quote request work item
      const res = await fetch(getApiUrl('/api/v1/work-items'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry.payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message = body?.error || 'Failed to sync quote request';
        if (res.status >= 400 && res.status < 500) {
          throw new Error(`NON_RETRYABLE:${message}`);
        }
        throw new Error(message);
      }

      const created = await res.json();

      // Create the relationship if specified
      if (entry.relationshipPayload && created.data?.id) {
        await fetch(getApiUrl('/api/v1/work-item-relationships'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...entry.relationshipPayload,
            target_work_item_id: created.data.id,
          }),
        }).catch(() => {
          // Relationship creation failure is non-critical
          console.warn('[SmartTaskOfflineQuoteRequests] Failed to create relationship for quote request');
        });
      }

      synced += 1;
    } catch (error: unknown) {
      failed += 1;
      const errMsg = error instanceof Error ? error.message : String(error);
      const nonRetryable = typeof errMsg === 'string' && errMsg.startsWith('NON_RETRYABLE:');
      const nextRetryCount = entry.retryCount + 1;
      if (!nonRetryable && nextRetryCount < MAX_QUOTE_REQUEST_RETRIES) {
        remaining.push({
          ...entry,
          retryCount: nextRetryCount,
          lastError: errMsg || 'Sync failed',
        });
      } else {
        if (!isSilentFetchError(error)) {
          console.error('[SmartTaskOfflineQuoteRequests] Dropping failed quote request:', errMsg || error);
        }
      }
    }
  }

  await savePendingQuoteRequests(clientId, remaining);
  return { synced, failed, remaining: remaining.length };
}
